import React, {useState, useEffect} from 'react';
import {
  Input,
  Container,
  Row,
  Col,
  Form
} from 'reactstrap'
import {FaSearch} from 'react-icons/fa'
import Layout from '../Layout/Layout'
import SearchCriteria from './SearchCriteria'
import Portal from '../../stacks/portal/PortalAWS'
import {useErrorToast} from '../../hooks/useErrorToast';
import searchCriteria from '../../data/searchCriteria'
import Pager from './Pager'
import SearchCardList from './SearchCardList'

function buildCriterion(criterion) {
  let rv = {
    [criterion.name]: {
      title: criterion.label,
      name: criterion.name,
    }
  }
  if (criterion.checkbox || criterion.radio) {
    rv[criterion.name].choices = criterion.selectValues.map((item, index) => {
      return {
        key: item,
        title: criterion.fieldLabel[index],
        value: false
      }
    })
  }
  return rv
}


const yearIncorporatedAttributes = {
  yearIncorporated: {
    title: 'Incorporated',
      choices: [
      {key:'<1990', value: false, title: '<1990'},
      {key:'<2000', value: false, title: '<2000'},
      {key:'<2010', value: false, title: '<2010'}
    ]
  }
}

const employeesAttributes = {
  employees: {
  title: 'Employees',
  choices: [
      {key:'<10', value: false, title: '<10'},
      {key:'<100', value: false, title: '<100'},
      {key:'<1000', value: false, title: '<1000'}
    ]
  }
}


const fundingAmountCurrency = {
  securitiesOfferedCount: {
    title: 'Funding Amount',
    choices: [
      {key:'<10000', value: false, title: '<$10,000'},
      {key:'<100000', value: false, title: '<$100,000'},
      {key:'<1000000', value: false, title: '<$1,000,000'},
      {key:'<10000000', value: false, title: '<$10,000,000'}
    ]
  }
}

const fullAttributes = {
  ...buildCriterion(searchCriteria.round),
  ...buildCriterion(searchCriteria.regType),
  ...buildCriterion(searchCriteria.securityTypes),
  ...buildCriterion(searchCriteria.legalForm),
  ...buildCriterion(searchCriteria.revenueProducing),
  ...buildCriterion(searchCriteria.profitable),
  ...buildCriterion(searchCriteria.industryNaics),
  ...yearIncorporatedAttributes,
  ...employeesAttributes,
  ...fundingAmountCurrency,
}

const SearchScreen = props => {

  const {setErrorMsg, errorToast} = useErrorToast('');

  const [, setLoading] = useState(false)
  const [, setLoadingMsg] = useState('Loading ...')
  const [searchString, setSearchString] = useState(props.searchString ? props.searchString : '')
  const [from, setFrom] = useState(0)
  const [total, setTotal] = useState()
  const [content, setContent] = useState({})
  const [attributes, setAttributes] = useState(fullAttributes)

  const [pageSize] = useState(12)
  const [pagesDisplayed] = useState(12)

  async function query(fm, attributes, search=searchString) {
    try {
      props.updateSearchString && props.updateSearchString(search)
      if (!fm) {
        setFrom(0);
        fm = 0;
      }
      if (!attributes) {
        attributes = generateAttributesSearch()
      }

      const portal = Portal.getInstance()
      setErrorMsg('')
      setLoading(true)
      setLoadingMsg(`Searching ...`)
      let attributedSearchString = search
      if (attributes.trim()) {
        if (attributedSearchString.trim()) {
          attributedSearchString += ' AND '
        }
        attributedSearchString += attributes
      }

      // const attributedSearchString = attributes ? `${searchString} AND ${attributes}` : searchString
      const data = await portal.search(attributedSearchString, pageSize, fm !== undefined ? fm : from)
      setTotal((data.hits && data.hits.total && data.hits.total.value) || 0)
      setContent(data)
      setLoading(false)
    }
    catch (err) {
      console.log(`INFO Portal search ${err.message}`)
      setLoading(false)
      setErrorMsg(err.message)
    }
  }

  useEffect(() => {
    query()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attributes])

  function queryFirst() {
    const newFrom = 0
    setFrom(newFrom)
    query(newFrom)
  }

  function queryPrev() {
    const newFrom = from - pageSize > 0 ? from - pageSize : 0
    setFrom(newFrom)
    query(newFrom)
  }

  function queryPage(page) {
    const pageFrom = page * pageSize
    if (pageFrom < total) {
      setFrom(pageFrom)
      query(pageFrom)
    }
  }

  function queryNext() {
    const newFrom = from + pageSize < total ? from + pageSize : from
    setFrom(newFrom)
    query(newFrom)
  }

  function queryLast() {
    const newFrom = Math.trunc(total / pageSize) * pageSize
    setFrom(newFrom)
    query(newFrom)
  }

  function morePages() {
    const totalPages = Math.trunc((total+pageSize-1)/pageSize)
    const basePage = from/pageSize
    if (basePage < totalPages - pagesDisplayed) {
      setFrom((basePage+pagesDisplayed)*pageSize)
    }
  }

  function generateSingleAttributeSearch(values) {
    const search = values.choices.reduce((current, {key, value}) => {
      let or = current !== '' ? ' OR ' : ''
      if (value) {
        return `${current}${or}${key}`
      }
      return current

    }, '')
    return search
  }

  function generateAttributesSearch() {
    const search = Object.entries(attributes).reduce((current, [key, attributeValue]) => {
      const criteria = generateSingleAttributeSearch(attributeValue)
      if (criteria) {
        let and = current !== '' ? ' AND ' : ''
        return `${current}${and}${key}: (${criteria})`
      }
      return current
    }, '')
    return search
  }

  function attributeOnChange(name, value) {
    const newValue = {...attributes[name], choices: value}
    setAttributes({...attributes, [name]: newValue})
  }

  function select(companyId, source) {
    props.setCurrentCompany && props.setCurrentCompany(companyId)
    props.updateCurrentState({currentCompany: {companyId, companyName: source.companyName}})
    props.history.push(`/display/${companyId}`)
  }

  function submitHandler(e) {
    e.preventDefault()
    query()
  }
  
  return (
    <Layout>
      <div style={{height: '1em'}}/>

      <Row>
        <Col md={3}>
          <SearchCriteria value={attributes} onChange={attributeOnChange}/>
        </Col>
        <Col md={9}>
        <Container>
          <Row>
            <Col>
              <p>
                <a
                  style={{color: 'red', textDecoration: 'underline'}}
                  href='https://youtu.be/eDqYHatJrE0'
                  target='_blank'
                  rel='noreferrer'
                >
                  Please watch this short instructional video to ensure your profile is properly set up.</a>
              </p>
            </Col>
          </Row>
          <Row>
            <Col md={10}>
              <Form onSubmit={submitHandler}>
                <Input value={searchString} onChange={(e) => setSearchString(e.target.value)}/>
              </Form>
            </Col>
            <Col md={2} onClick={() => query()}>
              <span>
                <FaSearch/>
              </span>
              <span
                style={{fontWeight: 'bold', textAlign: 'left', cursor: 'default'}}
                onClick={ () => {
                    setSearchString(''); query(0, generateAttributesSearch(), '')
                }}
              >
                &nbsp;&nbsp;Clear
              </span>
            </Col>
          </Row>
          <Row>
            <Col>
            { total === 0 ? 'Nothing Found' :
              <Pager
                basePage={from/pageSize}
                pagesDisplayed={(total-from)/pageSize < pagesDisplayed ? (total-from)/pageSize : pagesDisplayed}
                first={queryFirst}
                prev={queryPrev}
                page={queryPage}
                more={morePages}
                next={queryNext}
                last={queryLast}
              />
            }
            </Col>
          </Row>
          {content && content.hits && content.hits.hits
            && <SearchCardList hits={content.hits.hits} onClick={select}/>}
        </Container>
        </Col>
      </Row>
      {errorToast}
    </Layout>
  )
};

// const Copyright = styled.div`
//   position: fixed;
//   bottom: 0;
//   center: 0;
//   color: #efefef;
//   font-size: x-small;
// `


export default SearchScreen
